import { CheckIcon } from "@radix-ui/react-icons";
import { OnboardingStep } from "client/openapi/models/OnboardingStep";
import type { PersonRow } from "client/openapi/models/PersonRow";
import {
  FirstMeetingStepDialog,
  FormStepDialog,
  PaymentMethodStepDialog,
  PaymentPlanStepDialog,
} from "components/Onboarding";
import { prefetchTutors } from "pages/manage/students/[id]/StudentInfoTab/Tutors";
import { useState } from "react";
import AccountStepDialog from "./AccountStepDialog";
import BankLinkDialog from "./BankLinkDialog";
import TutorStepDialog from "./TutorStepDialog";

/** Each step's name, as the pills and the "Show people missing:" chips say it. */
export const STEP_LABELS: Record<OnboardingStep, string> = {
  [OnboardingStep.FORM]: "Form",
  [OnboardingStep.PAYMENT_METHOD]: "Payment method",
  [OnboardingStep.PAYMENT_PLAN]: "Payment plan",
  [OnboardingStep.TUTOR_ASSIGNED]: "Tutor Assigned",
  [OnboardingStep.FIRST_MEETING]: "First meeting",
  [OnboardingStep.ACCOUNT]: "Account",
  [OnboardingStep.BANK]: "Payroll",
};

/**
 * The window each step's pill opens: the thing if it's done, a way to get
 * it if not. Form, Payment method, Payment plan and First meeting are the
 * forms chat's (components/Onboarding); Tutor Assigned, Account and Payroll
 * are here.
 */
type StepWindow = (props: {
  row: PersonRow;
  orgId: number;
  done: boolean;
  onDone: () => void;
  onClose: () => void;
}) => JSX.Element;

const WINDOWS: Record<OnboardingStep, StepWindow> = {
  [OnboardingStep.FORM]: ({ row, ...props }) => (
    <FormStepDialog
      person={row}
      outstandingForms={row.onboarding?.outstanding_forms}
      {...props}
    />
  ),
  [OnboardingStep.PAYMENT_METHOD]: ({ row, ...props }) => (
    <PaymentMethodStepDialog person={row} {...props} />
  ),
  [OnboardingStep.PAYMENT_PLAN]: ({ row, ...props }) => (
    <PaymentPlanStepDialog person={row} {...props} />
  ),
  [OnboardingStep.TUTOR_ASSIGNED]: ({ row, orgId, onDone, onClose }) => (
    <TutorStepDialog
      student={row}
      orgId={orgId}
      onDone={onDone}
      onClose={onClose}
    />
  ),
  [OnboardingStep.FIRST_MEETING]: ({ row, ...props }) => (
    <FirstMeetingStepDialog person={row} {...props} />
  ),
  [OnboardingStep.ACCOUNT]: ({ row, orgId, done, onDone, onClose }) => (
    <AccountStepDialog
      tutor={row}
      orgId={orgId}
      done={done}
      onDone={onDone}
      onClose={onClose}
    />
  ),
  [OnboardingStep.BANK]: ({ row, orgId, done, onClose }) => (
    <BankLinkDialog
      tutor={row}
      hasAccount={!!row.onboarding?.steps[OnboardingStep.ACCOUNT]}
      done={done}
      orgId={orgId}
      onClose={onClose}
    />
  ),
};

/**
 * What a step's window loads, started as the pointer reaches its pill so
 * the window can open with its data.
 */
const PREFETCH: Partial<
  Record<OnboardingStep, (row: PersonRow, orgId: number) => void>
> = {
  [OnboardingStep.TUTOR_ASSIGNED]: (row, orgId) =>
    prefetchTutors(row.id, orgId),
};

/**
 * A person's onboarding steps as pills: green with a check when done,
 * yellow when not. Each opens its step's window; when the window finishes
 * something, the list reloads (the window stays open until closed).
 */
export default function OnboardingSteps({
  row,
  orgId,
  onChange,
}: {
  row: PersonRow;
  orgId: number;
  /** A window finished a step: reload. */
  onChange: () => void;
}) {
  const [open, setOpen] = useState<OnboardingStep>();
  const steps = (row.onboarding?.steps ?? {}) as Record<
    OnboardingStep,
    boolean
  >;
  const Window = open && WINDOWS[open];

  return (
    <>
      {Object.entries(steps).map(([key, done]) => {
        const step = key as OnboardingStep;
        return (
          <button
            key={step}
            type="button"
            className={`people-step${done ? " people-step--done" : ""}`}
            onPointerEnter={() => PREFETCH[step]?.(row, orgId)}
            onClick={() => setOpen(step)}
          >
            {done && <CheckIcon aria-hidden />}
            {STEP_LABELS[step]}
            <span className="sr-only">{done ? ": done" : ": not yet"}</span>
          </button>
        );
      })}
      {Window && (
        <Window
          row={row}
          orgId={orgId}
          done={!!steps[open]}
          onDone={onChange}
          onClose={() => setOpen(undefined)}
        />
      )}
    </>
  );
}
